export type UserRole = "admin" | "customer" | "subcustomer";

export type SessionUser = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  role: UserRole;
  active: boolean;
  parentCustomerId: string | null;
  impersonation?: {
    adminUserId: string;
    adminEmail: string | null;
    adminDisplayName: string | null;
    startedAt: string | null;
  } | null;
};

export type AuthResponse = {
  user: SessionUser;
};

export type RecognizedAccount = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  displayName: string;
  role: "customer" | "subcustomer";
  rememberedAt: string;
  lastUsedAt: string;
};

export type EmailVerificationRequiredResponse = {
  email: string;
  requiresEmailVerification: true;
};

export type SignInEmailTwoFactorResponse = {
  challenge: string;
  email: string;
  expiresAt: string;
  expiresInMinutes: number;
  requiresTwoFactor: true;
};

export type LoginResponse = AuthResponse | SignInEmailTwoFactorResponse;
export type RegisterResponse = AuthResponse | EmailVerificationRequiredResponse;

export type LabelInventorySummary = {
  assigned: number;
  available: number;
  availableBalance: number;
  consumed: number;
  expired: number;
  exported: number;
  externalUsed: number;
  total: number;
  voided: number;
};

export type DashboardSummary = {
  role: UserRole;
  user: SessionUser;
  totals: {
    customers?: number;
    subcustomers?: number;
    stores: number;
    orders: number;
    shipments: number;
    availableLabels: number;
    totalLabels?: number;
    usedLabels?: number;
    voidedLabels?: number;
    expiredLabels?: number;
    invoices: number;
  };
  labelInventory?: LabelInventorySummary;
  operations?: {
    activeCustomers30?: number;
    availableLabels?: number;
    carrierCheckErrors: number;
    delivered: number;
    inTransit: number;
    labelsUsedMonth?: number;
    labelsUsedToday?: number;
    lowBalanceCustomers?: number;
    lowBalanceProducts?: number;
    lowStockProducts?: number;
    openOrders?: number;
    pendingInvoicesGross?: number;
    problemShipments: number;
    returnShipments: number;
    shipments30: number;
    shipments7: number;
    shipmentsToday: number;
    waitingForLabelOrders?: number;
  };
  performance?: {
    dhl: {
      checked: number;
      clean: number;
      deliveredHints: number;
      errors: number;
      faulty: number;
      returnHints: number;
      used: number;
    };
    shipments: {
      created: number;
      labelFailed: number;
      labelPending: number;
      printed: number;
      returns: number;
      shopifyFailed: number;
      shopifySuccess: number;
      total: number;
      trackingDisabled: number;
      trackingFailed: number;
      trackingPending: number;
      trackingSuccess: number;
      voided: number;
    };
  };
  recentActivity: Array<{
    id: string;
    label: string;
    detail: string;
    createdAt: string;
  }>;
};

export type StoreSummary = {
  id: string;
  name: string;
  platform: string;
  amazonV2Mode?: "shadow" | "pilot" | "active" | "paused" | null;
  active: boolean;
  storeUrl: string | null;
  ordersCount: number;
  ordersSyncedAt: string | null;
  orderSyncEnabled: boolean;
  orderSyncIntervalMinutes: number;
  orderSyncNextRunAt: string | null;
  orderSyncLastAttemptAt: string | null;
  orderSyncLastSuccessAt: string | null;
  orderSyncLastErrorAt: string | null;
  orderSyncLastError: string | null;
  orderSyncLastStatus: string;
  orderSyncConsecutiveFailures: number;
  plentyTrackingWriteEnabled: boolean;
  plentySyncRange: "1d" | "3d" | "7d" | "30d" | "3m" | "all";
  syncReady?: boolean;
  autoLabelProductByWeightEnabled?: boolean;
  orderDisplayCountryCodes?: string[];
  hideCompletedOrders?: boolean;
  woocommerceCountryFilterEnabled?: boolean;
  woocommerceCountryFilterCountryCodes?: string[];
  createdAt: string;
  updatedAt: string;
  ebayStatus: string | null;
  ebayUsername: string | null;
};

export type StoreCredentialField = {
  configured: boolean;
  preview: string | null;
  secret: boolean;
};

export type StoreSenderAddressSummary = {
  id: string | null;
  senderName: string | null;
  street1: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  active: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  deactivatedAt: string | null;
};

export type StoreSenderAddressPayload = {
  senderName: string;
  street1: string;
  postalCode: string;
  city: string;
  country: string;
};

export type StoreDetail = StoreSummary & {
  externalStoreId: string | null;
  ordersJsonS3Key: string | null;
  ordersJsonSizeBytes: number;
  ordersJsonEtag: string | null;
  shopifyApiVersion: string | null;
  shopifyFulfillmentSyncEnabled: boolean;
  shopwareMarkShipped: boolean;
  afterbuyInvoiceAutomationEnabled: boolean;
  afterbuyInvoiceTriggerTag: string | null;
  skuOnLabelEnabled: boolean;
  onlineSellerLabelPrefixEnabled: boolean;
  autoLabelProductByWeightEnabled: boolean;
  credentials: {
    id: string | null;
    configured: boolean;
    updatedAt: string | null;
    fields: Record<string, StoreCredentialField>;
  };
  senderAddressMode: "global" | "store_override";
  globalSenderAddress: StoreSenderAddressSummary;
  activeSenderAddress: StoreSenderAddressSummary | null;
  senderAddressHistory: StoreSenderAddressSummary[];
};

export type StoreSyncLog = {
  id: string;
  trigger: string;
  status: string;
  platform: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  fetchedCount: number;
  insertedCount: number;
  updatedCount: number;
  errorMessage: string | null;
  summary: string | null;
  syncRange?: string | null;
  scannedCount?: number | null;
  matchedCount?: number | null;
  pagesFetched?: number | null;
  requestsMade?: number | null;
};

export type OrderSyncPlatform = "afterbuy" | "amazon" | "amazon_v2" | "etsy" | "shopify" | "shopware" | "billbee" | "aboutyou" | "otto" | "woocommerce" | "kaufland" | "opencart" | "plenty";

export type AmazonBridgeJob = {
  jobId: string;
  platform?: "amazon" | "etsy" | null;
  siteName: string;
  status: string;
  createdAt: number | null;
  expiresAt: number | null;
  authorizationUrl?: string | null;
  amazonAuthUrl: string | null;
  etsyAuthUrl?: string | null;
  message: string | null;
  result: Record<string, unknown> | null;
  rawPageSnippet?: string | null;
};

export type AmazonConnectionState = {
  provider: string | null;
  platform?: "amazon" | "etsy" | null;
  siteName: string | null;
  status: string | null;
  jobId: string | null;
  createdAt: number | null;
  expiresAt: number | null;
  authorizationUrl?: string | null;
  amazonAuthUrl: string | null;
  etsyAuthUrl?: string | null;
  message: string | null;
  lastError: string | null;
  result: Record<string, unknown> | null;
  rawPageSnippet: string | null;
  lastApiResponse: Record<string, unknown> | null;
  errorCode: string | null;
  retryable: boolean | null;
  shouldContactAdmin: boolean | null;
  userMessage: string | null;
  operatorAction: string | null;
  incidentId: string | null;
  incidentPath: string | null;
  systemStatusUrl: string | null;
  latestIncidentUrl: string | null;
  updatedAt: string | null;
  completedAt: string | null;
  active: boolean;
  success: boolean;
  waitingForConfirmation: boolean;
  expired: boolean;
};

export type AmazonStoreSummary = {
  id: string;
  name: string;
  active: boolean;
  storeUrl: string | null;
  externalStoreId: string | null;
  ordersCount: number;
  lastSyncedAt: string | null;
  ordersMeta: Record<string, unknown> | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type AmazonStatusResponse = {
  ok: true;
  bridge: {
    baseUrl: string;
    reachable: boolean;
    ok: boolean;
    busy: boolean;
    activeJob: AmazonBridgeJob | null;
    error: string | null;
    errorCode?: string | null;
    retryable?: boolean | null;
    shouldContactAdmin?: boolean | null;
    userMessage?: string | null;
    operatorAction?: string | null;
    incidentId?: string | null;
    incidentPath?: string | null;
    systemStatusUrl?: string | null;
    latestIncidentUrl?: string | null;
  };
  store: AmazonStoreSummary | null;
  connection: AmazonConnectionState | null;
};

export type EtsyBrowserConnectionPayload = {
  browserPort?: string;
  browserProfile: string;
  password: string;
  siteName: string;
  storeId?: string;
  username: string;
};

export type StorePlatform =
  | "afterbuy"
  | "amazon"
  | "amazon_v2"
  | "etsy"
  | "aboutyou"
  | "billbee"
  | "ebay"
  | "jtl"
  | "kaufland"
  | "manual"
  | "opencart"
  | "otto"
  | "plenty"
  | "shopify"
  | "shopware"
  | "woocommerce";

export type CreateStorePayload = Record<string, string> & {
  displayName: string;
  platform: StorePlatform;
};

export type OrderSummary = {
  id: string;
  previewOnly?: boolean;
  previewUrl?: string;
  address1?: string | null;
  address2?: string | null;
  addressAdjustments?: string[];
  addressHasBlockingWarning?: boolean;
  addressWarnings?: string[];
  currency?: string | null;
  email?: string | null;
  externalOrderId: string | null;
  fulfillmentStatus?: string | null;
  itemCount?: number | null;
  itemsPreview?: Array<{
    asin?: string;
    grossTotal?: number | null;
    quantity: number;
    sku?: string;
    title: string;
    unitGross?: number | null;
  }>;
  orderNumber: string;
  orderedAt?: string | null;
  paymentStatus?: string | null;
  postalCode?: string | null;
  recipientName: string;
  city: string;
  countryCode: string;
  status: string;
  sourcePlatform: string;
  sourceStoreId?: string | null;
  sourceStoreName?: string | null;
  salesChannel?: string | null;
  selectedAddressSource?: "shipping" | "billing_fallback" | "unknown";
  billingAddressDiffers?: boolean;
  createdAt: string;
  archivedAt?: string | null;
  archivedByUserId?: string | null;
  archiveReason?: string | null;
  labelGenerationStatus?: string | null;
  labelProductName?: string | null;
  lastShipmentAt?: string | null;
  lastShipmentId?: string | null;
  shipmentCount?: number;
  storeName: string | null;
  totalPrice?: number | null;
  trackingNumber?: string | null;
  weightGrams?: number;
};

export type OrderAddressSnapshot = {
  recipientName: string | null;
  address1: string | null;
  address2: string | null;
  postalCode: string | null;
  city: string | null;
  countryCode: string | null;
  email: string | null;
  phone: string | null;
};

export type OrderDetailShipment = {
  id: string;
  trackingNumber: string | null;
  status: string | null;
  labelGenerationStatus: string | null;
  labelGenerationError: string | null;
  labelDownloadUrl: string | null;
  labelPdfDownloadUrl: string | null;
  labelZipDownloadUrl: string | null;
  labelProductId: string | null;
  labelProvider: string | null;
  sourcePlatformTrackingSyncStatus: string | null;
  sourcePlatformTrackingSyncMessage: string | null;
  sourcePlatformTrackingSyncedAt: string | null;
  shippedAt: string | null;
  createdAt: string;
  updatedAt: string;
  labelProductName: string | null;
  labelProductCarrier: string | null;
};

export type OrderDetail = Omit<OrderSummary, "storeName"> & {
  address1: string;
  address2: string | null;
  email: string | null;
  phone: string | null;
  postalCode: string;
  shippedAt: string | null;
  storeId: string | null;
  updatedAt: string;
  weightGrams: number;
  shippingAddress: OrderAddressSnapshot | null;
  billingAddress: OrderAddressSnapshot | null;
  store: {
    id: string;
    name: string | null;
    platform: string | null;
    storeUrl: string | null;
  } | null;
  shipments: OrderDetailShipment[];
};

export type CarrierDeliveryEvent = {
  at: string | null;
  text: string;
  location: string | null;
  statusCode: string | null;
  statusText: string | null;
  isReturn: boolean;
};

export type ShipmentDetail = {
  id: string;
  trackingNumber: string | null;
  rawTrackingNumber?: string | null;
  status: string | null;
  shipmentType: string | null;
  labelGenerationStatus: string | null;
  labelGenerationError: string | null;
  labelGeneratedAt: string | null;
  labelDownloadUrl: string | null;
  labelPdfDownloadUrl: string | null;
  labelZipDownloadUrl: string | null;
  labelObjectUrl: string | null;
  labelOutputKey: string | null;
  labelProvider: string | null;
  hasEmbeddedPdf: boolean;
  labelHtmlAvailable: boolean;
  sourcePlatformTrackingSyncStatus: string | null;
  sourcePlatformTrackingSyncMessage: string | null;
  sourcePlatformTrackingSyncedAt: string | null;
  shopifyFulfillmentSyncStatus: string | null;
  shopifyFulfillmentSyncError: string | null;
  shopifyFulfillmentSyncedAt: string | null;
  carrierCheckStatus?: string | null;
  carrierCheckCheckedAt?: string | null;
  carrierCheckMessage?: string | null;
  carrierDeliveryStatus?: string | null;
  carrierDeliveryLabel?: string | null;
  carrierDeliverySeverity?: "neutral" | "success" | "warning" | "danger" | string | null;
  carrierDeliveryStatusAt?: string | null;
  carrierDeliveryTerminal?: boolean;
  carrierDeliveryLastEventAt?: string | null;
  carrierTrackingLink?: string | null;
  carrierEvents?: CarrierDeliveryEvent[];
  carrierRemediationStatus?: string | null;
  carrierRemediationMessage?: string | null;
  replacementLabelSourceId?: string | null;
  normalizedCarrierStatus?: {
    key: string;
    label: string;
    severity: "neutral" | "success" | "warning" | "danger";
    explanation: string;
  };
  shippedAt: string | null;
  createdAt: string;
  updatedAt: string;
  order: {
    id: string;
    orderNumber: string | null;
    externalOrderId: string | null;
    recipientName: string | null;
    recipientCompany?: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    postalCode: string | null;
    countryCode: string | null;
    email: string | null;
    phone: string | null;
    sourcePlatform: string | null;
    selectedAddressSource?: "shipping" | "billing_fallback" | "unknown";
    billingAddressDiffers?: boolean;
    status: string | null;
    weightGrams: number;
    store: {
      id: string;
      name: string | null;
      platform: string | null;
    } | null;
  };
  labelProduct: {
    id: string;
    name: string | null;
    carrier: string | null;
    weightMinGrams: number;
    weightMaxGrams: number;
  } | null;
};

export type AdminCustomerDetail = {
  customer: {
    id: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
    role: UserRole;
    active: boolean;
    emailVerified: boolean;
    emailTwoFactorEnabled: boolean;
    phone: string | null;
    taxId: string | null;
    billingStreet1: string | null;
    billingPostalCode: string | null;
    billingCity: string | null;
    billingCountry: string | null;
    senderNameOverride: string | null;
    senderStreet1: string | null;
    senderPostalCode: string | null;
    senderCity: string | null;
    senderCountry: string | null;
    parentCustomerId: string | null;
    teamOwnerId: string | null;
    ownerId: string;
    createdAt: string;
    updatedAt: string;
    lastSignedIn: string | null;
  };
  totals: {
    storesCount: number;
    ordersCount: number;
    shipmentsCount: number;
    availableLabels: number;
    totalLabels?: number;
    usedLabels?: number;
    voidedLabels?: number;
    expiredLabels?: number;
    invoicesCount: number;
    teamMembersCount: number;
  };
  labelInventory?: LabelInventorySummary;
  stores: StoreSummary[];
  teamMembers: TeamMemberSummary[];
  balances: Array<{
    id: string;
    quantityAvailable: number;
    updatedAt: string | null;
    labelProductId: string;
    labelProductName: string;
    carrier: string;
  }>;
  purchases: Array<{
    id: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    status: string | null;
    type: string | null;
    createdAt: string;
    labelProductName: string | null;
    createdByEmail: string | null;
    shopifyOrderName: string | null;
    availableQuantity: number;
    usedQuantity: number;
    voidedQuantity: number;
    expiredQuantity: number;
  }>;
  orders: OrderSummary[];
  shipments: Array<{
    id: string;
    trackingNumber: string | null;
    status: string | null;
    labelGenerationStatus: string | null;
    sourcePlatformTrackingSyncStatus: string | null;
    createdAt: string;
    orderNumber: string | null;
    labelProductName: string | null;
  }>;
  invoices: Array<{
    id: string;
    invoiceNumber: string | null;
    status: string | null;
    grossTotal: number;
    currency: string | null;
    issuedAt: string | null;
    createdAt: string;
    storeName: string | null;
  }>;
};

export type LabelProductSummary = {
  id: string;
  name: string;
  carrier: string;
  active: boolean;
  basePrice?: number;
  customerPrice?: number | null;
  effectivePrice: number;
  price?: number;
  stockAvailable?: number;
  weightMinGrams: number;
  weightMaxGrams: number;
};

export type LabelBalanceSummary = {
  id: string;
  labelProductId: string;
  labelProductName: string;
  carrier: string;
  quantityAvailable: number;
  updatedAt?: string | null;
  weightMinGrams: number;
  weightMaxGrams: number;
};

export type LabelBalanceAdjustmentSummary = {
  id: string;
  carrier: string | null;
  createdAt: string | null;
  delta: number;
  labelProductId: string | null;
  labelProductName: string | null;
  reason: string | null;
  weightMinGrams: number;
  weightMaxGrams: number;
};

export type LabelPurchaseSummary = {
  id: string;
  bonus?: boolean;
  carrier: string | null;
  createdAt: string | null;
  labelProductId: string | null;
  labelProductName: string | null;
  notes: string | null;
  quantity: number;
  status: string | null;
  totalPrice: number;
  type: string | null;
  unitPrice: number;
  weightMinGrams: number;
  weightMaxGrams: number;
};

export type LabelBalancesResponse = {
  balances: LabelBalanceSummary[];
  labelInventory?: LabelInventorySummary;
  productTotals: Array<{
    labelProductId: string | null;
    labelProductName: string | null;
    lastPurchaseAt: string | null;
    purchaseCount: number;
    purchasedQuantity: number;
    purchasedTotalPrice: number;
  }>;
  recentAdjustments: LabelBalanceAdjustmentSummary[];
  recentPurchases: LabelPurchaseSummary[];
  totals: {
    lastActivityAt: string | null;
    lastBalanceUpdateAt: string | null;
    lastPurchaseAt: string | null;
    productCount: number;
    purchaseCount: number;
    purchasedQuantity: number;
    purchasedTotalPrice: number;
    totalAvailable: number;
    totalLabels?: number;
    usedLabels?: number;
    voidedLabels?: number;
    expiredLabels?: number;
  };
};

export type AdminCustomerActionResponse = {
  ok: true;
  active?: boolean;
  alreadyVerified?: boolean;
  customerId: string;
  emailSent?: boolean;
  emailTwoFactorEnabled?: boolean;
  emailVerified?: boolean;
  expiresAt?: string;
  message?: string;
};

export type TeamMemberSummary = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  active: boolean;
  createdAt: string | null;
  lastSignedIn: string | null;
};

export type ProfileDetails = {
  billingCity: string | null;
  billingCountry: string | null;
  billingPostalCode: string | null;
  billingStreet1: string | null;
  defaultLabelProductId: string | null;
  defaultShipmentType: "outbound" | "return" | string;
  emailTwoFactorEnabled: boolean;
  emailVerified: boolean;
  phone: string | null;
  senderCity: string | null;
  senderCountry: string | null;
  senderNameOverride: string | null;
  senderPostalCode: string | null;
  senderStreet1: string | null;
  taxId: string | null;
};

export type ProfileResponse = {
  user: SessionUser;
  profile: ProfileDetails;
};

export type SupportRequestSummary = {
  id: string;
  category: string;
  companyName: string | null;
  createdAt: string | null;
  email: string | null;
  errorCode: string | null;
  message: string;
  pagePath: string | null;
  priority: string;
  referenceId: string | null;
  senderName: string;
  subject: string;
};

export type TrainingVideoSummary = {
  id: string;
  available: boolean;
  category: string;
  description: string;
  language: string;
  title: string;
  url: string;
};

export type AffiliateProfileSummary = {
  id: string;
  createdAt?: string | null;
  defaultCommissionRate: number;
  referralCode: string;
  referralLink?: string;
  status: string;
  usageCommissionAmount?: number | null;
};

export type AffiliateDashboardResponse = {
  profile: AffiliateProfileSummary | null;
  totals: {
    approved: number;
    paid: number;
    pending: number;
    rejected: number;
    referrals: number;
    voided: number;
  };
  referrals: Array<{
    id: string;
    approvedCommission: number;
    calculationType: string | null;
    commissionAmount: number | null;
    commissionEventCount: number;
    commissionRate: number | null;
    effectiveFrom: string | null;
    effectiveUntil: string | null;
    paidCommission: number;
    pendingCommission: number;
    monthPurchasedQuantity: number;
    monthPurchasedTotal: number;
    monthUsedLabels: number;
    purchasedQuantity: number;
    purchasedTotal: number;
    referralCodeUsed: string | null;
    referredAt: string | null;
    referredUserCompanyName: string | null;
    referredUserCreatedAt: string | null;
    referredUserEmail: string | null;
    status: string | null;
    totalCommission: number;
    usedLabels: number;
  }>;
  events: Array<{
    id: string;
    amount: number;
    approvedAt?: string | null;
    createdAt: string | null;
    currency: string | null;
    eventKey?: string | null;
    externalOrderId?: string | null;
    paidAt?: string | null;
    paymentReference?: string | null;
    rate: number;
    referredUserCompanyName?: string | null;
    referredUserEmail: string | null;
    rejectedAt?: string | null;
    source?: string | null;
    status: string | null;
    statusReason?: string | null;
    voidedAt?: string | null;
  }>;
  labelCredit: AffiliateLabelCreditSnapshot | null;
};

export type AffiliateLabelCreditProduct = {
  availableStock: number;
  carrier: string | null;
  grossUnitPrice: number;
  id: string;
  name: string;
  netUnitPrice: number;
  weightMaxGrams: number;
  weightMinGrams: number;
};

export type AffiliateLabelCreditRedemptionSummary = {
  amount: number;
  completedAt: string | null;
  createdAt: string;
  errorMessage: string | null;
  grossUnitPrice: number;
  id: string;
  labelProductId: string;
  labelPurchaseId: string | null;
  netUnitPrice: number;
  productName: string | null;
  quantity: number;
  source: string;
  status: string;
  updatedAt: string;
};

export type AffiliateLabelCreditSnapshot = {
  approvedAvailable: number;
  convertedTotal: number;
  pendingAvailable: number;
  products: AffiliateLabelCreditProduct[];
  redemptions: AffiliateLabelCreditRedemptionSummary[];
};

export type AffiliateLabelCreditRedemptionResponse = {
  ok: boolean;
  replayed: boolean;
  redemption: AffiliateLabelCreditRedemptionSummary;
};

export type AdminAffiliateLabelCreditResponse = {
  affiliates: Array<{
    approved: number;
    companyName: string | null;
    email: string;
    firstName: string | null;
    id: string;
    lastName: string | null;
    pending: number;
    referralCode: string | null;
  }>;
  labelCredit: AffiliateLabelCreditSnapshot | null;
  ok: true;
  selectedAffiliateUserId: string | null;
};

export type CreateShipmentResponse = {
  createdCount?: number;
  ok: boolean;
  shipmentId: string;
  orderId: string;
  trackingNumber: string;
  labelDownloadUrl: string | null;
  labelGenerationStatus: "pending" | "success" | "failed" | string;
  error?: string;
  labelCreditRestored?: boolean;
  orderArchived?: boolean;
  sourcePlatformTrackingSyncStatus?: string;
  failedCount?: number;
  packageCount?: number;
  requestedCount?: number;
  shipments?: Array<CreateShipmentResponse & { packageIndex?: number; packageTotal?: number }>;
  trackingNumbers?: string[];
  warning?: string;
};

export type ResourcePayload = Record<string, unknown>;

export type ActivityLogPayload = {
  action: string;
  entityId?: string;
  entityLabel?: string;
  entityType?: string;
  metadata?: Record<string, unknown>;
  pagePath?: string;
};

export type AddressSuggestion = {
  address1: string;
  city: string;
  postalCode: string;
  countryCode: string;
  display: string;
};

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
};

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export type AdminLabelCreditCartOperationResponse =
  | {
      ok: true;
      cartId: string;
      idempotencyKey: string;
      invoice: null | {
        grossTotal: number;
        invoiceId: string;
        invoiceNumber: string;
        lineCount: number;
        pdfDownloadUrl?: string;
        status: string;
      };
      invoiceError: string | null;
      replayed: boolean;
      result: { count: number; results: Array<Record<string, unknown>> };
      status: "completed";
      summary: { grossTotal: number; lineCount: number; netTotal: number; quantity: number; vatTotal: number };
    }
  | {
      ok: true;
      cartId: string;
      idempotencyKey: string;
      processing: true;
      status: "processing";
    }
  | {
      ok: false;
      cartId: string;
      error: string;
      idempotencyKey: string;
      status: "failed";
    };

async function requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(path, {
    method: options.method ?? "GET",
    headers: options.body ? { "Content-Type": "application/json" } : undefined,
    body: options.body ? JSON.stringify(options.body) : undefined,
    credentials: "same-origin",
  });

  const payload = (await response.json().catch(() => null)) as { error?: string } | T | null;

  if (!response.ok) {
    const message =
      payload && typeof payload === "object" && "error" in payload && typeof payload.error === "string"
        ? payload.error
        : "İstek tamamlanamadı.";
    throw new ApiError(message, response.status);
  }

  return payload as T;
}

async function requestFormJson<T>(path: string, body: FormData): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    body,
    credentials: "same-origin",
  });

  const payload = (await response.json().catch(() => null)) as { error?: string } | T | null;

  if (!response.ok) {
    const message =
      payload && typeof payload === "object" && "error" in payload && typeof payload.error === "string"
        ? payload.error
        : "İstek tamamlanamadı.";
    throw new ApiError(message, response.status);
  }

  return payload as T;
}

export type AmazonV2ConnectionSummary = {storeId: string; name: string; sellerId: string; marketplaceId: string; mode: "shadow" | "pilot" | "active" | "paused"; status: string; verifiedAt: string | null; lastSyncAt: string | null; errorCode: string | null};
export type AmazonV2JobSummary = {id: string; shipment_id: string; amazon_order_id: string; tracking_number: string; state: string; last_error_code: string | null};
export type AmazonV2Status = {enabled: boolean; setupRequired: boolean; companyAssigned: boolean; legacyAccounts?: Array<{storeId: string; name: string}>; selectedStoreId?: string; nextSnapshotCursor?: string | null; connections: AmazonV2ConnectionSummary[]; snapshots: Array<{amazonOrderId: string; orderId: string | null; orderStatus?: string | null; orderedAt?: string | null; itemCount?: number | null; state: string; blockCode: string | null; recipientName: string; city: string; items: Array<{title: string; sku: string; quantity: number}>}>; jobs: AmazonV2JobSummary[]};
export type AmazonV2ConnectionEvent = {
  id: string; owner_user_id: string; store_id: string | null; attempt_id: string | null;
  email: string; company_name: string | null; store_name: string | null; marketplace_id: string | null;
  stage: string; outcome: string; error_code: string | null; authorization_host: string | null;
  created_at: string; record_source: string;
  details: {listed?: number; stored?: number; detailsFetched?: number; imported?: number; summaryOnly?: number; detailsPending?: number; importsPending?: number; listingPending?: boolean; rateLimited?: boolean; range?: string; expiresAt?: string; providerHttpStatus?: number};
};
export type AmazonV2AdminStatus = {setupRequired: boolean; enabled: boolean; writesEnabled?: boolean; contractVerified?: boolean; guardsEnabled?: boolean; customers: Array<{id: string; email: string; company_name: string | null; role?: UserRole}>; companies: Array<{owner_user_id: string; company_id: number; email: string}>; connections: Array<{store_id: string; owner_user_id: string; name: string; email: string; seller_id: string; marketplace_id: string; mode: string; status: string; legacy_store_id: string | null; last_error_code: string | null}>; legacyStores: Array<{id: string; user_id: string; name: string}>; jobs: Array<AmazonV2JobSummary & {request_json: string; owner_user_id: string; store_id: string}>; connectionEvents?: AmazonV2ConnectionEvent[]};
export const apiClient = {
  amazonV2Status: (storeId?: string, snapshotCursor?: string) => requestJson<AmazonV2Status>(`/api/amazon-v2/status?${new URLSearchParams({...(storeId ? {storeId} : {}), ...(snapshotCursor ? {snapshotCursor} : {})})}`),
  startAmazonV2: (payload: {name?: string; sellerId?: string; marketplaceId?: string; storeId?: string; legacyStoreId?: string; newAccount?: boolean}) => requestJson<{storeId: string; authorizationUrl: string; expiresAt: string}>("/api/amazon-v2/start", {method: "POST", body: payload}),
  amazonV2Action: (action: "verify" | "sync" | "pause" | "disconnect" | "resume-shadow" | "release-tracking" | "prepare-tracking", payload: Record<string, unknown>) => requestJson<Record<string, unknown>>(`/api/amazon-v2/${action}`, {method: "POST", body: payload}),
  amazonV2AdminStatus: (eventOwnerId?: string) => requestJson<AmazonV2AdminStatus>(`/api/admin/amazon-v2${eventOwnerId ? `?eventOwnerId=${encodeURIComponent(eventOwnerId)}` : ""}`),
  amazonV2AdminAction: (payload: Record<string, unknown>) => requestJson<Record<string, unknown>>("/api/admin/amazon-v2", {method: "POST", body: payload}),
  me: () => requestJson<AuthResponse>("/api/auth/me"),
  resource: <T extends ResourcePayload>(path: string) => requestJson<T>(path),
  trackActivity: (payload: ActivityLogPayload) =>
    requestJson<{ ok: true; id: string; requestId: string }>("/api/activity", {
      method: "POST",
      body: payload,
    }),
  dashboard: () => requestJson<DashboardSummary>("/api/dashboard"),
  stores: (options: { includeInactive?: boolean } = {}) =>
    requestJson<{ stores: StoreSummary[] }>(`/api/stores${options.includeInactive ? "?includeInactive=1" : ""}`),
  store: (storeId: string) => requestJson<{ ok: true; store: StoreDetail }>(`/api/stores/${encodeURIComponent(storeId)}`),
  storeSyncLogs: (storeId: string) =>
    requestJson<{ ok: true; store: StoreSummary; logs: StoreSyncLog[] }>(`/api/stores/${encodeURIComponent(storeId)}/sync-logs`),
  storeSenderAddresses: (storeId: string) =>
    requestJson<{
      ok: true;
      senderAddressMode: StoreDetail["senderAddressMode"];
      globalSenderAddress: StoreSenderAddressSummary;
      activeSenderAddress: StoreSenderAddressSummary | null;
      senderAddressHistory: StoreSenderAddressSummary[];
    }>(`/api/stores/${encodeURIComponent(storeId)}/sender-addresses`),
  createStoreSenderAddress: (storeId: string, payload: StoreSenderAddressPayload) =>
    requestJson<{ ok: true; store: StoreDetail }>(`/api/stores/${encodeURIComponent(storeId)}/sender-addresses`, {
      method: "POST",
      body: payload,
    }),
  activateStoreSenderAddress: (storeId: string, addressId: string) =>
    requestJson<{ ok: true; store: StoreDetail }>(`/api/stores/${encodeURIComponent(storeId)}/sender-addresses/${encodeURIComponent(addressId)}/activate`, {
      method: "POST",
    }),
  useGlobalStoreSenderAddress: (storeId: string) =>
    requestJson<{ ok: true; store: StoreDetail }>(`/api/stores/${encodeURIComponent(storeId)}/sender-addresses/use-global`, {
      method: "POST",
    }),
  createStore: (payload: CreateStorePayload) =>
    requestJson<{ ok: true; store: { id: string; name: string; platform: StorePlatform } }>("/api/stores", {
      method: "POST",
      body: payload,
    }),
  updateStore: (storeId: string, payload: Record<string, unknown>) =>
    requestJson<{ ok: true; store: StoreDetail }>(`/api/stores/${encodeURIComponent(storeId)}`, {
      method: "PATCH",
      body: payload,
    }),
  deleteCustomerStore: (storeId: string) =>
    requestJson<{ ok: boolean; deleted?: boolean; error?: string; storeId?: string; storeName?: string; platform?: string }>("/api/delete-customer-store", {
      method: "POST",
      body: { storeId },
    }),
  runDueOrderSync: (limit = 10, platform?: OrderSyncPlatform | string) =>
    requestJson<Record<string, unknown>>(`/api/admin/order-sync-run-due?limit=${encodeURIComponent(String(limit))}${platform ? `&platform=${encodeURIComponent(String(platform))}` : ""}`, {
      method: "POST",
    }),
  adminSyncStoreOrders: (storeId: string, platform?: OrderSyncPlatform | string, syncRange?: string) =>
    requestJson<{
      ok: boolean;
      platform: OrderSyncPlatform;
      fetched: number;
      imported: number;
      inserted: number;
      updated: number;
      s3Key?: string;
      syncedAt?: string;
      message?: string;
      error?: string;
    }>("/api/admin/stores/sync", {
      method: "POST",
      body: { storeId, platform, syncRange },
    }),
  orders: (query = "") => requestJson<{ orders: OrderSummary[] }>(`/api/orders${query}`),
  order: (orderId: string) => requestJson<{ ok: true; order: OrderDetail }>(`/api/orders/${encodeURIComponent(orderId)}`),
  archiveOrder: (orderId: string) =>
    requestJson<{ ok: true; archivedAt?: string | null }>(`/api/orders/${encodeURIComponent(orderId)}/archive`, {
      method: "POST",
    }),
  bulkArchiveOrders: (orderIds: string[]) =>
    requestJson<{ archived: number; limit: number; ok: true; requested: number; skipped: number }>("/api/orders/bulk-archive", {
      method: "POST",
      body: { orderIds },
    }),
  restoreOrder: (orderId: string) =>
    requestJson<{ ok: true }>(`/api/orders/${encodeURIComponent(orderId)}/restore`, {
      method: "POST",
    }),
  shipment: (shipmentId: string) => requestJson<{ ok: true; shipment: ShipmentDetail }>(`/api/shipments/${encodeURIComponent(shipmentId)}`),
  adminCustomer: (customerId: string) =>
    requestJson<{ ok: true } & AdminCustomerDetail>(`/api/admin/customers/${encodeURIComponent(customerId)}`),
  createAdminCustomer: (payload: { companyName?: string; email: string; firstName?: string; lastName?: string; password?: string }) =>
    requestJson<{ ok: true; customer: ResourcePayload }>("/api/admin/customers", {
      method: "POST",
      body: payload,
    }),
  ebayStatus: () =>
    requestJson<{
      envReady: boolean;
      envError: string | null;
      connections: Array<{
        storeId: string;
        name: string;
        status: string;
        ebayUsername: string | null;
        ordersCount: number;
        ordersSyncedAt: string | null;
        refreshTokenExpiresAt: string | null;
      }>;
    }>("/api/ebay/status"),
  syncEbay: (storeId: string, syncRange = "3m") =>
    requestJson<{
      ok: true;
      fetched: number;
      inserted: number;
      updated: number;
      pagesFetched: number;
      syncedAt: string;
    }>("/api/ebay/sync", {
      method: "POST",
      body: { storeId, syncRange },
    }),
  testJtl: (storeId: string) =>
    requestJson<{
      ok: boolean;
      connectionMode: "platform" | "ip";
      testedAt?: string;
      requiresBridge?: boolean;
      message?: string;
      ordersCount?: number | null;
      sampleOrderId?: string | null;
    }>("/api/jtl/test", {
      method: "POST",
      body: { storeId },
    }),
  syncJtl: (storeId: string, syncRange = "30d") =>
    requestJson<{
      ok: boolean;
      connectionMode: "platform" | "ip";
      imported?: number;
      inserted?: number;
      updated?: number;
      cachedOrders?: number;
      externallyManaged?: boolean;
      requiresBridge?: boolean;
      connectorUrl?: string | null;
      message?: string;
    }>("/api/jtl/sync", {
      method: "POST",
      body: { storeId, syncRange },
    }),
  syncStoreOrders: (storeId: string, platform: OrderSyncPlatform, syncRange?: string) =>
    requestJson<{
      ok: boolean;
      platform: OrderSyncPlatform;
      fetched: number;
      imported: number;
      inserted: number;
      updated: number;
      s3Key?: string;
      syncedAt?: string;
      message?: string;
    }>(`/api/${platform}/sync`, {
      method: "POST",
      body: { storeId, syncRange },
    }),
  testAfterbuy: (storeId: string) =>
    requestJson<{ ok: boolean; testedAt?: string }>("/api/afterbuy/test", {
      method: "POST",
      body: { storeId },
    }),
  testShopware: (storeId: string) =>
    requestJson<{ ok: boolean; orderCount?: number; testedAt?: string }>("/api/shopware/test", {
      method: "POST",
      body: { storeId },
    }),
  marketplaceStatus: (platform: "amazon" | "etsy", storeId?: string, options?: { newConnection?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (storeId) searchParams.set("storeId", storeId);
    if (options?.newConnection) searchParams.set("new", "1");
    const query = searchParams.toString();
    return requestJson<AmazonStatusResponse>(`/api/${platform}/status${query ? `?${query}` : ""}`);
  },
  startMarketplaceConnection: (platform: "amazon" | "etsy", payload: { siteName: string; storeId?: string }) =>
    requestJson<{
      ok: true;
      storeId: string;
      job: AmazonBridgeJob;
      store: AmazonStoreSummary | null;
      connection: Record<string, unknown>;
    }>(`/api/${platform}/start`, {
      method: "POST",
      body: payload,
    }),
  confirmMarketplaceConnection: (platform: "amazon" | "etsy", payload: { storeId: string; jobId: string }) =>
    requestJson<{
      ok: true;
      storeId: string;
      job: AmazonBridgeJob;
      connection: Record<string, unknown>;
    }>(`/api/${platform}/click`, {
      method: "POST",
      body: payload,
    }),
  disconnectMarketplace: (platform: "amazon" | "etsy", storeId: string) =>
    requestJson<{ ok: true; disconnected: boolean; storeId: string; siteName: string; active: boolean }>(`/api/${platform}/disconnect`, {
      method: "POST",
      body: { storeId },
    }),
  amazonStatus: (storeId?: string) =>
    requestJson<AmazonStatusResponse>(`/api/amazon/status${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ""}`),
  startAmazonConnection: (payload: { siteName: string; storeId?: string }) =>
    requestJson<{
      ok: true;
      storeId: string;
      job: AmazonBridgeJob;
      store: AmazonStoreSummary | null;
      connection: Record<string, unknown>;
    }>("/api/amazon/start", {
      method: "POST",
      body: payload,
    }),
  confirmAmazonConnection: (payload: { storeId: string; jobId: string }) =>
    requestJson<{
      ok: true;
      storeId: string;
      job: AmazonBridgeJob;
      connection: Record<string, unknown>;
    }>("/api/amazon/click", {
      method: "POST",
      body: payload,
    }),
  disconnectAmazon: (storeId: string) =>
    requestJson<{ ok: true; disconnected: boolean; storeId: string; siteName: string; active: boolean }>("/api/amazon/disconnect", {
      method: "POST",
      body: { storeId },
    }),
  etsyStatus: (storeId?: string, options?: { newConnection?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (storeId) searchParams.set("storeId", storeId);
    if (options?.newConnection) searchParams.set("new", "1");
    const query = searchParams.toString();
    return requestJson<AmazonStatusResponse & { source?: AmazonStatusResponse["bridge"] }>(`/api/etsy/status${query ? `?${query}` : ""}`);
  },
  startEtsyConnection: (payload: EtsyBrowserConnectionPayload) =>
    requestJson<{
      ok: true;
      storeId: string;
      store: AmazonStoreSummary | null;
      connection: Record<string, unknown>;
    }>("/api/etsy/start", {
      method: "POST",
      body: payload,
    }),
  confirmEtsyConnection: (payload: { storeId: string; jobId: string }) =>
    requestJson<{
      ok: true;
      storeId: string;
      job: AmazonBridgeJob;
      connection: Record<string, unknown>;
    }>("/api/etsy/click", {
      method: "POST",
      body: payload,
    }),
  disconnectEtsy: (storeId: string) =>
    requestJson<{ ok: true; disconnected: boolean; storeId: string; siteName: string; active: boolean }>("/api/etsy/disconnect", {
      method: "POST",
      body: { storeId },
    }),
  searchGermanAddress: (query: string) =>
    requestJson<{ ok: true; suggestions: AddressSuggestion[]; warning?: string }>("/api/address/search", {
      method: "POST",
      body: { query },
    }),
  lookupGermanPostalCode: (postalCode: string) =>
    requestJson<{ ok: boolean; postalCode: string; city: string | null; localities: Array<{ name: string; postalCode: string }> }>(
      `/api/address/postal-code?postalCode=${encodeURIComponent(postalCode)}`
    ),
  validateGermanAddress: (payload: {
    recipientName?: string;
    address1?: string;
    address2?: string;
    city?: string;
    postalCode?: string;
    countryCode?: string;
  }) =>
    requestJson<{
      ok: true;
      isValid: boolean;
      addressBlocked: boolean;
      blockReason: string | null;
      warnings: string[];
      suggestions: AddressSuggestion[];
      normalizedAddress: {
        recipientName: string;
        address1: string;
        address2: string;
        city: string;
        postalCode: string;
        countryCode: string;
      };
    }>("/api/address/validate", {
      method: "POST",
      body: payload,
    }),
  contactSupport: (payload: { category?: string; message: string; pageUrl?: string; priority?: string; subject: string }) =>
    requestJson<{ ok: true; emailSent: boolean; referenceId: string; supportEmail: string; warning: string | null }>("/api/support/contact", {
      method: "POST",
      body: payload,
    }),
  adminSupport: () =>
    requestJson<{
      ok: true;
      dailyReport: Array<{ customerCount: number; day: string | null; highCount: number; latestAt: string | null; requestCount: number; urgentCount: number }>;
      requests: SupportRequestSummary[];
      summary: { last24hCount: number; last7dCount: number; priorityCount: number; todayCount: number; totalCount: number };
    }>("/api/admin/support"),
  trainingVideos: () => requestJson<{ ok: true; videos: TrainingVideoSummary[] }>("/api/training-videos"),
  reportAppError: (payload: {
    area?: string;
    errorCode?: string;
    message: string;
    pagePath?: string;
    scope?: "customer" | "admin" | "system";
    technicalMessage?: string;
  }) =>
    requestJson<{ ok: true; referenceId: string }>("/api/app-error", {
      method: "POST",
      body: payload,
    }),
  labelProducts: () => requestJson<{ products: LabelProductSummary[] }>("/api/label-products"),
  labelProductsForPurchase: () =>
    requestJson<{
      products: LabelProductSummary[];
      billingEnabled: boolean;
      billingMissing: { accessToken: boolean; shopDomain: boolean; signingSecret: boolean };
      stock: Record<string, number>;
    }>("/api/label-products"),
  createLabelCheckout: (payload: { items?: Array<{ labelProductId: string; quantity: number }>; labelProductId?: string; quantity?: number }) =>
    requestJson<{
      ok: true;
      status: "checkout_created";
      checkoutToken: string;
      checkoutUrl: string;
      currency: string;
      draftOrderId: string;
      grossTotal?: number;
      items?: Array<{
        grossTotal?: number;
        grossUnitPrice?: number;
        labelProductId: string;
        labelProductName?: string;
        netTotal?: number;
        netUnitPrice?: number;
        quantity: number;
        taxRate?: number;
        taxTotal?: number;
        totalPrice: number;
        unitPrice: number;
      }>;
      netTotal?: number;
      quantity: number;
      taxRate?: number;
      taxTotal?: number;
      totalPrice: number;
    }>("/api/label-purchases", {
      method: "POST",
      body: { ...payload, mode: "createCheckout" },
    }),
  confirmLabelCheckout: (payload: { checkoutToken: string; draftOrderId: string }) =>
    requestJson<{
      ok: true;
      status: "paid_and_allocated" | "already_processed" | "pending_payment" | string;
      message?: string;
      newBalance?: number;
      purchaseId?: string;
      purchaseIds?: string[];
      quantity?: number;
      grossTotal?: number;
      netTotal?: number;
      taxTotal?: number;
      totalPrice?: number;
    }>("/api/label-purchases", {
      method: "POST",
      body: { ...payload, mode: "confirmPayment" },
    }),
  reconcileLabelPurchases: (payload: { sinceDays?: number } = {}) =>
    requestJson<{
      ok: true;
      status: string;
      scannedCount: number;
      matchedCount: number;
      allocatedCount?: number;
      alreadyProcessedCount?: number;
      skippedCount?: number;
      results?: Array<{ status?: string; quantity?: number }>;
    }>("/api/label-purchases", {
      method: "POST",
      body: { ...payload, mode: "reconcileRecentPayments" },
    }),
  balances: () => requestJson<LabelBalancesResponse>("/api/balances"),
  team: () => requestJson<{ ok: true; teamMembers: TeamMemberSummary[] }>("/api/team"),
  createTeamMember: (payload: { email: string; firstName?: string; lastName?: string; password: string }) =>
    requestJson<{ ok: true; teamMember: TeamMemberSummary; message: string }>("/api/team", {
      method: "POST",
      body: payload,
    }),
  updateTeamMember: (payload: { active?: boolean; firstName?: string; lastName?: string; teamMemberId: string }) =>
    requestJson<{ ok: true; teamMember: TeamMemberSummary; message: string }>("/api/team/update", {
      method: "POST",
      body: payload,
    }),
  setTeamMemberPassword: (payload: { newPassword: string; teamMemberId: string }) =>
    requestJson<{ ok: true; message: string }>("/api/team/password", {
      method: "POST",
      body: payload,
    }),
  profile: () => requestJson<ProfileResponse>("/api/profile"),
  updateProfile: (payload: Record<string, unknown>) =>
    requestJson<ProfileResponse & { ok: true }>("/api/profile", {
      method: "POST",
      body: payload,
    }),
  updateProfilePassword: (payload: { currentPassword: string; newPassword: string }) =>
    requestJson<{ ok: true; message: string }>("/api/profile/password", {
      method: "POST",
      body: payload,
    }),
  checkLabelCarrier: (labelSourceId: string) =>
    requestJson<{
      ok: boolean;
      runId: string;
      requestId: string;
      status: string;
      labelSource: ResourcePayload;
      result: ResourcePayload;
    }>("/api/admin/labels/carrier-check", {
      method: "POST",
      body: { labelSourceId },
    }),
  checkLabelCarriers: (labelSourceIds: string[]) =>
    requestJson<{
      ok: boolean;
      runId: string;
      requestId: string;
      status: string;
      requestedCount: number;
      labelSourceIds: string[];
    }>("/api/admin/labels/carrier-check-bulk", {
      method: "POST",
      body: { labelSourceIds },
    }),
  releasePendingLabels: () =>
    requestJson<{
      ok: true;
      releasedCount: number;
      remainingPendingCount: number;
    }>("/api/admin/labels/release-pending", {
      method: "POST",
    }),
  releaseLabelToAvailable: (labelSourceId: string) =>
    requestJson<{
      ok: true;
      labelSourceIds: string[];
      message: string;
      productStock: Record<string, number>;
      reactivatedCount: number;
      status: "available";
      trackingNumbers: string[];
    }>("/api/admin/labels/release-available", {
      method: "POST",
      body: { labelSourceId },
    }),
  removeLabelSourceFromInventory: (labelSourceId: string, reason?: string) =>
    requestJson<{
      ok: true;
      labelProductId: string;
      labelSourceId: string;
      message: string;
      status: "void";
      trackingNumber: string;
    }>("/api/admin/labels/remove", {
      method: "POST",
      body: { labelSourceId, reason },
    }),
  reactivateConsumedLabels: (labelSourceIds: string[], reason?: string) =>
    requestJson<{
      ok: true;
      labelSourceIds: string[];
      message: string;
      productStock: Record<string, number>;
      reactivatedCount: number;
      status: "available";
      trackingNumbers: string[];
    }>("/api/admin/labels/reactivate-consumed", {
      method: "POST",
      body: { labelSourceIds, reason },
    }),
  reactivateAdminLabels: (labelSourceIds: string[], reason?: string, allowExpiredPdfOverride = false, partial = false) =>
    requestJson<{
      ok: true;
      labelSourceIds: string[];
      message: string;
      pdfExpiryOverrideCount: number;
      pdfExpirySanitizedCount: number;
      productStock: Record<string, number>;
      reactivatedCount: number;
      outcomes?: Array<{ id: string; ok: boolean; message?: string }>;
      status: "available";
      trackingNumbers: string[];
    }>("/api/admin/labels/reactivate", {
      method: "POST",
      body: { allowExpiredPdfOverride, labelSourceIds, reason, partial },
    }),
  runDueCarrierCheck: (mode: "assigned" | "available" | "consumed") =>
    requestJson<ResourcePayload>("/api/admin/carrier-check-run-due", {
      method: "POST",
      body: { mode },
    }),
  adminCustomerLabelPrices: (customerId: string) =>
    requestJson<{
      ok: true;
      customerId: string;
      prices: Array<{
        active: boolean;
        basePrice: number;
        carrier: string | null;
        customerId: string;
        id: string | null;
        labelProductActive: boolean;
        labelProductId: string;
        labelProductName: string;
        price: number | null;
        updatedAt: string | null;
      }>;
    }>(`/api/admin/customer-label-prices?customerId=${encodeURIComponent(customerId)}`),
  saveAdminCustomerLabelPrices: (payload: { customerId: string; items: Array<{ active?: boolean; labelProductId: string; price: number | null }> }) =>
    requestJson<{
      ok: true;
      createdCount: number;
      deletedCount: number;
      updatedCount: number;
      historicalRepricing?: { updatedCount?: number };
    }>("/api/admin/customer-label-prices", {
      method: "POST",
      body: payload,
    }),
  adminVerifyCustomerEmail: (customerId: string) =>
    requestJson<AdminCustomerActionResponse>("/api/admin/customers/verify-email", {
      method: "POST",
      body: { customerId },
    }),
  adminSendCustomerVerificationEmail: (customerId: string) =>
    requestJson<AdminCustomerActionResponse>("/api/admin/customers/send-verification-email", {
      method: "POST",
      body: { customerId },
    }),
  adminSendCustomerPasswordReset: (customerId: string) =>
    requestJson<AdminCustomerActionResponse>("/api/admin/customers/send-password-reset", {
      method: "POST",
      body: { customerId },
    }),
  adminSetCustomerStatus: (payload: { active: boolean; customerId: string }) =>
    requestJson<AdminCustomerActionResponse>("/api/admin/customers/status", {
      method: "POST",
      body: payload,
    }),
  adminDisableCustomerEmailTwoFactor: (customerId: string) =>
    requestJson<AdminCustomerActionResponse>("/api/admin/customers/disable-email-two-factor", {
      method: "POST",
      body: { customerId },
    }),
  allocateAdminLabelCreditCart: (payload: {
    createInvoice?: boolean;
    customerId: string;
    idempotencyKey: string;
    invoiceStatus?: string;
    items: Array<{
      bonus?: boolean;
      labelProductId: string;
      notes?: string;
      quantity: number;
      totalPrice?: number;
      unitPrice?: number;
    }>;
    notes?: string;
    orderNumber?: string;
  }) =>
    requestJson<AdminLabelCreditCartOperationResponse>("/api/admin/labels/cart-allocate", {
      method: "POST",
      body: payload,
    }),
  adminLabelCreditCartOperation: (idempotencyKey: string) =>
    requestJson<AdminLabelCreditCartOperationResponse>(
      `/api/admin/labels/cart-allocate?idempotencyKey=${encodeURIComponent(idempotencyKey)}`
    ),
  createAdminBillingExpense: (payload: {
    amount: number;
    category: "lost_package" | "other" | "penalty" | "return_package";
    description?: string;
    expenseDate: string;
  }) =>
    requestJson<{ ok: true; expense: ResourcePayload }>("/api/admin/billing-expenses", {
      method: "POST",
      body: payload,
    }),
  deleteAdminBillingExpense: (id: string) =>
    requestJson<{ ok: true; id: string }>("/api/admin/billing-expenses", {
      method: "DELETE",
      body: { id },
    }),
  updateCommissionStatus: (payload: {
    action: "approve" | "paid" | "reject" | "void";
    commissionEventId: string;
    paymentReference?: string;
    reason?: string;
  }) =>
    requestJson<{ action: string; commissionEventId: string; ok: true; status: string }>("/api/admin/commissions", {
      method: "POST",
      body: payload,
    }),
  adminAffiliateLabelCredits: (affiliateUserId = "") =>
    requestJson<AdminAffiliateLabelCreditResponse>(
      `/api/admin/affiliate-label-credits${affiliateUserId ? `?affiliateUserId=${encodeURIComponent(affiliateUserId)}` : ""}`
    ),
  redeemAdminAffiliateLabelCredits: (payload: {
    affiliateUserId: string;
    allowPending: boolean;
    idempotencyKey: string;
    labelProductId: string;
    quantity: number;
  }) =>
    requestJson<AffiliateLabelCreditRedemptionResponse>("/api/admin/affiliate-label-credits", {
      method: "POST",
      body: { action: "redeem", ...payload },
    }),
  grantAdminAffiliateBonus: (payload: {
    affiliateUserId: string;
    amount: number;
    idempotencyKey: string;
    reason: string;
  }) =>
    requestJson<{ amount: number; eventId: string; ok: true; replayed: boolean; status: string }>(
      "/api/admin/affiliate-label-credits",
      { method: "POST", body: { action: "grant_bonus", ...payload } }
    ),
  assignAdminAffiliateReferral: (payload: {
    affiliateUserId: string;
    backfill?: boolean;
    commissionRate?: number;
    commissionType: "fixed" | "rate";
    effectiveFrom?: string;
    effectiveUntil?: string;
    referredUserIds: string[];
    usageCommissionAmount?: number;
  }) =>
    requestJson<{
      ok: true;
      created: boolean;
      createdCount: number;
      effectiveFrom: string;
      effectiveUntil: string;
      updatedCount: number;
      backfill: {
        adjusted: number;
        amount: number;
        created: number;
        results: ResourcePayload[];
      };
      referral: ResourcePayload;
      referrals: ResourcePayload[];
    }>("/api/admin/affiliates", {
      method: "POST",
      body: payload,
    }),
  completeAdminAffiliateHistory: (referralId: string) =>
    requestJson<{ ok: true; created: number; amount: number; approved: number; effectiveFrom: string; scanned: number; alreadyRecorded: number }>("/api/admin/affiliates", {
      method: "POST", body: { action: "complete_history", referralId },
    }),
  endAdminAffiliateReferral: (referralId: string, reason: string, voidUnpaid = false) =>
    requestJson<{ ok: true; voided: number; protectedEvents: number }>("/api/admin/affiliates", {
      method: "POST", body: { action: "end_referral", referralId, reason, voidUnpaid },
    }),
  compensateAdminExpiredLabels: () => requestJson<{ compensated: number; remaining: number; errors?: Array<{ message: string }> }>("/api/admin/expired-labels", {
    method: "POST", body: { action: "compensate", limit: 25 },
  }),
  createAffiliateProfile: () =>
    requestJson<{ ok: true; created: boolean; profile: AffiliateProfileSummary }>("/api/affiliate", {
      method: "POST",
    }),
  affiliate: () => requestJson<AffiliateDashboardResponse>("/api/affiliate"),
  affiliateLabelCredits: () =>
    requestJson<{ labelCredit: AffiliateLabelCreditSnapshot; ok: true }>("/api/affiliate/label-credits"),
  redeemAffiliateLabelCredits: (payload: { idempotencyKey: string; labelProductId: string; quantity: number }) =>
    requestJson<AffiliateLabelCreditRedemptionResponse>("/api/affiliate/label-credits", {
      method: "POST",
      body: payload,
    }),
  createAdminPlan: (payload: { active?: boolean; description?: string; name: string }) =>
    requestJson<{ ok: true; plan: ResourcePayload }>("/api/admin/plans", {
      method: "POST",
      body: payload,
    }),
  upsertAdminInvoice: (payload: Record<string, unknown>) =>
    requestJson<{
      ok: true;
      edited: boolean;
      grossTotal: number;
      invoiceId: string;
      invoiceNumber: string;
      lineCount: number;
      pdfDownloadUrl?: string;
      pdfSavedKey?: string;
      status: string;
    }>(payload.invoiceId ? `/api/admin/invoices/${encodeURIComponent(String(payload.invoiceId))}` : "/api/admin/invoices", {
      method: payload.invoiceId ? "PATCH" : "POST",
      body: payload,
    }),
  createAdminManualShopifyInvoice: (payload: {
    customerId: string;
    lines: Array<{ quantity: number; taxRate?: number; title: string; unitPrice: number }>;
    note?: string;
    paymentMethodName?: string;
    priceMode: "net";
  }) =>
    requestJson<{
      ok: true;
      status: "shopify_order_created";
      draftOrderId: string;
      grossTotal: number;
      invoiceNumber: string;
      netTotal: number;
      paymentMethodName: string;
      priceMode: "gross" | "net";
      purchaseIds: string[];
      quantity: number;
      shopifyOrderId: string;
      shopifyOrderName: string;
      taxTotal: number;
    }>("/api/admin/shopify-manual-invoices", {
      method: "POST",
      body: payload,
    }),
  cancelAdminManualShopifyInvoice: (payload: { labelPurchaseId?: string; shopifyOrderId?: string }) =>
    requestJson<{
      alreadyCancelled: boolean;
      customerId: string;
      labelPurchaseId: string;
      ok: true;
      purchaseCount: number;
      shopifyOrderId: string;
      shopifyOrderName?: string | null;
      status: "cancelled";
    }>("/api/admin/shopify-manual-invoices/cancel", {
      method: "POST",
      body: payload,
    }),
  generateAdminInvoicePdf: (invoiceId: string) =>
    requestJson<{ ok: true; invoiceId: string; invoiceNumber: string; pdfDownloadUrl: string; pdfSavedKey: string; pdfSizeBytes: number }>("/api/admin/invoice-pdf", {
      method: "POST",
      body: { invoiceId },
    }),
  generateInvoicePdf: (invoiceId: string) =>
    requestJson<{ ok: true; invoiceId: string; invoiceNumber: string; pdfDownloadUrl: string; pdfSavedKey: string; pdfSizeBytes: number }>("/api/invoice-pdf", {
      method: "POST",
      body: { invoiceId },
    }),
  generateLabelPurchaseInvoicePdf: (labelPurchaseId: string, admin = false) =>
    requestJson<{ ok: true; downloadUrl?: string; fileName: string; invoiceNumber: string; labelPurchaseId: string; pdfSavedKey: string; signedDownloadUrl: string }>(
      admin ? "/api/admin/label-purchase-invoices" : "/api/label-purchase-invoices",
      {
        method: "POST",
        body: admin ? { mode: "generatePdf", labelPurchaseId } : { labelPurchaseId },
      }
    ),
  updateLabelPurchaseInvoiceOverride: (payload: { labelPurchaseId: string; quantity: number; totalPrice: number; unitPrice: number }) =>
    requestJson<{ ok: true; invoiceNumber: string; labelPurchaseId: string; quantity: number; totalPrice: number; unitPrice: number }>("/api/admin/label-purchase-invoices", {
      method: "POST",
      body: { ...payload, mode: "updateOverride" },
    }),
  generateInvoiceForOrder: (payload: { externalOrderId?: string; finalize?: boolean; orderId?: string; storeId?: string }) =>
    requestJson<{ ok: true; alreadyExists: boolean; invoiceId: string; invoiceNumber: string; pdfDownloadUrl?: string; pdfSavedKey?: string; status: string }>("/api/invoices/generate", {
      method: "POST",
      body: payload,
    }),
  syncShipmentTracking: (shipmentId: string) =>
    requestJson<{ attempted: boolean; message?: string; status: string }>("/api/shipments/sync-tracking", {
      method: "POST",
      body: { shipmentId },
    }),
  cancelShipment: (shipmentId: string) =>
    requestJson<{ message?: string; success?: boolean; warning?: string }>("/api/shipments/cancel", {
      method: "POST",
      body: { shipmentId },
    }),
  adminCancelShipment: (shipmentId: string) =>
    requestJson<{ message?: string; success?: boolean; warning?: string }>("/api/admin/shipments/cancel", {
      method: "POST",
      body: { shipmentId },
    }),
  regenerateShipment: (payload: {
    address1?: string;
    address2?: string;
    city?: string;
    confirmed?: boolean;
    countryCode?: string;
    postalCode?: string;
    recipientName?: string;
    shipmentId: string;
  }) =>
    requestJson<CreateShipmentResponse>("/api/shipments/regenerate", {
      method: "POST",
      body: payload,
    }),
  adminSyncShipmentTracking: (shipmentId: string) =>
    requestJson<{ attempted: boolean; message?: string; status: string }>("/api/admin/shipments/sync-tracking", {
      method: "POST",
      body: { shipmentId },
    }),
  bulkSyncShipmentTracking: (payload: { limit?: number; shipmentIds?: string[]; syncStatus?: string }, admin = false) =>
    requestJson<{ ok: true; queued: true; requestedCount: number; markedPending: number; shipmentIds: string[] }>(
      admin ? "/api/admin/shipments/sync-tracking-bulk" : "/api/shipments/sync-tracking-bulk",
      {
        method: "POST",
        body: payload,
      }
    ),
  amazonDhlGermanyRepairStatus: () =>
    requestJson<{
      ok: true;
      status: { failed: number; pending: number; remaining: number; repaired: number; retryableFailed: number; total: number };
    }>("/api/admin/shipments/repair-amazon-dhl-germany"),
  repairAmazonDhlGermanyBatch: (payload: { batchSize?: number; retryFailed?: boolean } = {}) =>
    requestJson<{
      ok: true;
      processed: number;
      results: Array<{ id: string; message?: string | null; status: "failed" | "success" }>;
      status: { failed: number; pending: number; remaining: number; repaired: number; retryableFailed: number; total: number };
    }>("/api/admin/shipments/repair-amazon-dhl-germany", {
      method: "POST",
      body: payload,
    }),
  previewLabelInventoryExport: (payload: {
    externalState?: string;
    labelProductId?: string;
    labelSourceIds?: string[];
    search?: string;
    status?: string;
  }) =>
    requestJson<{
      ok: true;
      requestedCount: number;
      externallyExportedCount: number;
      maxExportLabels: number;
    }>("/api/label-inventory-export-preview", {
      method: "POST",
      body: payload,
    }),
  exportLabelInventory: (payload: {
    allowRepeatedExternalExport?: boolean;
    externalState?: string;
    fileName?: string;
    format?: "csv" | "zip";
    labelProductId?: string;
    labelSourceIds?: string[];
    markExported?: boolean;
    search?: string;
    status?: string;
  }) =>
    requestJson<{ ok: true; downloadUrl: string; exportedCount: number; format?: "csv" | "zip"; requestId: string; resultKey: string; sizeBytes: number; skippedCount?: number }>("/api/label-inventory/export", {
      method: "POST",
      body: payload,
    }),
  manageAssignedLabelSelection: (payload: {
    action: "replace" | "return";
    labelSourceIds: string[];
    overrideRecentCustomerHold?: boolean;
    overrideReason?: string;
  }) =>
    requestJson<{
      action: "replace" | "return";
      ok: true;
      replacedCount: number;
      replacementLabelSourceIds: string[];
      recentCustomerHoldOverrideCount: number;
      returnedCount: number;
      selectedCount: number;
    }>("/api/label-inventory/manage", {
      method: "POST",
      body: payload,
    }),
  runAdminLabelPurchaseMaintenance: (payload: Record<string, unknown>) =>
    requestJson<Record<string, unknown>>("/api/admin/label-purchases/maintenance", {
      method: "POST",
      body: payload,
    }),
  syncAdminShopifyBilling: (payload: Record<string, unknown>) =>
    requestJson<Record<string, unknown>>("/api/admin/shopify-billing-sync", {
      method: "POST",
      body: payload,
    }),
  runInvoiceSelfTest: () =>
    requestJson<Record<string, unknown>>("/api/admin/invoices/self-test", {
      method: "POST",
      body: {},
    }),
  runAdminDataSeed: (payload: Record<string, unknown>) =>
    requestJson<Record<string, unknown>>("/api/admin/data-maintenance/seed-demo", {
      method: "POST",
      body: payload,
    }),
  runAdminWeightFieldCleanup: (payload: Record<string, unknown>) =>
    requestJson<Record<string, unknown>>("/api/admin/data-maintenance/fix-weight-fields", {
      method: "POST",
      body: payload,
    }),
  adminLabelProducts: () => requestJson<{ ok: true; products: ResourcePayload[] }>("/api/admin/label-products"),
  adminLabelProduct: (labelProductId: string) =>
    requestJson<{ ok: true; product: ResourcePayload }>(`/api/admin/label-products/${encodeURIComponent(labelProductId)}`),
  manageAdminSelectedLabels: (payload: {
    action: "assign" | "return";
    customerId: string;
    labelSourceIds: string[];
    reason?: string;
  }) =>
    requestJson<{
      allocatedLabelSourceIds?: string[];
      customerId: string;
      labelProductId?: string;
      newBalance?: number;
      ok: true;
      quantity?: number;
      returnedCount?: number;
      selectedCount?: number;
      stockRemaining?: number;
    }>("/api/admin/labels/selected", {
      method: "POST",
      body: payload,
    }),
  verifyAdminLabelPdfExpiries: (payload: { labelProductId?: string; limit?: number } = {}) =>
    requestJson<{
      checkedCount: number;
      errorCount: number;
      expiredCount: number;
      ok: boolean;
      remainingCount: number;
      requestedCount: number;
    }>("/api/admin/label-sources/verify-expiry", {
      method: "POST",
      body: payload,
    }),
  createAdminLabelProduct: (payload: Record<string, unknown>) =>
    requestJson<{ ok: true; product: ResourcePayload }>("/api/admin/label-products", {
      method: "POST",
      body: payload,
    }),
  updateAdminLabelProduct: (labelProductId: string, payload: Record<string, unknown>) =>
    requestJson<{ ok: true; product: ResourcePayload }>(`/api/admin/label-products/${encodeURIComponent(labelProductId)}`, {
      method: "PATCH",
      body: payload,
    }),
  uploadLabelSources: (formData: FormData) =>
    requestFormJson<{
      ok: true;
      count: number;
      carrierCheckQueued?: number;
      persisted: number;
      skipped: number;
      results: ResourcePayload[];
    }>("/api/upload-labels", formData),
  createShipmentForOrder: (payload: {
    addressOverride?: {
      address1?: string;
      address2?: string;
      city?: string;
      countryCode?: string;
      postalCode?: string;
      recipientName?: string;
    };
    confirmed?: boolean;
    labelProductId: string;
    orderId: string;
    packageCount?: number;
    shipmentType?: string;
    storeId?: string;
  }) =>
    requestJson<CreateShipmentResponse>("/api/shipments/create-for-order", {
      method: "POST",
      body: payload,
    }),
  replaceShipmentTracking: (payload: { confirmed?: boolean; shipmentId: string }) =>
    requestJson<{
      cancelledShipmentId: string;
      labelDownloadUrl: string | null;
      newShipmentId: string;
      newTrackingNumber: string;
      ok: true;
      oldTrackingNumber: string;
      sourcePlatformTrackingSyncStatus?: string;
      warning?: string;
    }>("/api/shipments/replace", {
      method: "POST",
      body: payload,
    }),
  bulkCreateShipmentsForOrders: (payload: {
    concurrency?: number;
    labelProductId?: string;
    autoSelectByWeight?: boolean;
    orderConfigs?: Array<{
      addressOverride?: {
        address1?: string;
        address2?: string;
        city?: string;
        countryCode?: string;
        postalCode?: string;
        recipientName?: string;
      };
      labelProductId: string;
      orderId: string;
    }>;
    orderIds?: string[];
    storeId?: string;
  }) =>
    requestJson<{
      ok: boolean;
      error?: string;
      results: Array<Partial<CreateShipmentResponse> & { error?: string; labelProductId?: string; ok: boolean; orderId: string; recovered?: boolean; skipped?: boolean; skipReason?: string }>;
      summary: {
        creditsByProduct?: Array<{ available: number; labelProductId: string; requested: number }>;
        failed: number;
        recovered?: number;
        skipped: number;
        success: number;
        total: number;
      };
    }>("/api/shipments/bulk-create", {
      method: "POST",
      body: payload,
    }),
  createManualShipment: (payload: {
    address1: string;
    address2?: string;
    city: string;
    companyName?: string;
    countryCode?: string;
    email?: string;
    labelProductId: string;
    orderNumber?: string;
    phone?: string;
    postalCode: string;
    recipientName: string;
    senderName?: string;
    shipmentType?: string;
  }) =>
    requestJson<CreateShipmentResponse>("/api/shipments/create-manual", {
      method: "POST",
      body: payload,
    }),
  bulkCreateManualShipments: (payload: {
    labelProductId?: string;
    rows: Array<{
      address1: string;
      address2?: string;
      city: string;
      companyName?: string;
      countryCode?: string;
      email?: string;
      labelProductId?: string;
      orderNumber?: string;
      packageCount?: number;
      phone?: string;
      postalCode: string;
      recipientName: string;
      senderName?: string;
      shipmentType?: string;
    }>;
    shipmentType?: string;
  }) =>
    requestJson<{
      ok: boolean;
      creditsByProduct?: Array<{ available: number; labelProductId: string; requested: number }>;
      error?: string;
      results: Array<Partial<CreateShipmentResponse> & { error?: string; ok: boolean; rowIndex?: number; packageIndex?: number; packageTotal?: number }>;
      summary: { failed: number; success: number; total: number };
    }>("/api/shipments/bulk-manual", {
      method: "POST",
      body: payload,
    }),
  login: (email: string, password: string, rememberDevice = false) =>
    requestJson<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: { email, password, rememberDevice },
    }),
  recognizedAccounts: () =>
    requestJson<{ accounts: RecognizedAccount[] }>("/api/auth/recognized-accounts"),
  loginRecognizedAccount: (userId: string) =>
    requestJson<AuthResponse>("/api/auth/recognized-login", {
      method: "POST",
      body: { userId },
    }),
  removeRecognizedAccount: (userId: string) =>
    requestJson<{ ok: true; removed: boolean }>("/api/auth/recognized-account/remove", {
      method: "POST",
      body: { userId },
    }),
  resendEmailTwoFactor: (challenge: string) =>
    requestJson<SignInEmailTwoFactorResponse>("/api/auth/email-two-factor/resend", {
      method: "POST",
      body: { challenge },
    }),
  verifyEmailTwoFactor: (payload: { challenge: string; code: string; rememberDevice?: boolean }) =>
    requestJson<AuthResponse>("/api/auth/email-two-factor/verify", {
      method: "POST",
      body: payload,
    }),
  forgotPassword: (email: string) =>
    requestJson<{ ok: true; message: string }>("/api/auth/forgot-password", {
      method: "POST",
      body: { email },
    }),
  resetPassword: (payload: { password: string; token: string }) =>
    requestJson<{ ok: true }>("/api/auth/reset-password", {
      method: "POST",
      body: payload,
    }),
  sendVerificationEmail: (email: string) =>
    requestJson<{ ok: true; message: string }>("/api/auth/send-verify-email", {
      method: "POST",
      body: { email },
    }),
  verifyEmail: (code: string) =>
    requestJson<{ ok: true }>("/api/auth/verify-email", {
      method: "POST",
      body: { code },
    }),
	register: (payload: {
	    email: string;
	    password: string;
	    firstName?: string;
	    lastName?: string;
	    companyName?: string;
	    phone: string;
	    referralCode?: string;
	  }) =>
    requestJson<RegisterResponse>("/api/auth/register", {
      method: "POST",
      body: payload,
    }),
  logout: () => requestJson<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  impersonateCustomer: (customerId: string) =>
    requestJson<{ ok: true; redirect: string; user: SessionUser }>("/api/admin/impersonate", {
      method: "POST",
      body: { customerId },
    }),
  stopImpersonation: () =>
    requestJson<{ ok: true; redirect: string; restored: boolean }>("/api/admin/stop-impersonation", {
      method: "POST",
    }),
};
